/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, lazy, Suspense } from 'react';
import './App.scss';
import FMLoader from 'src/components/FMLoader';
import {
  Route,
  Routes,
  Navigate,
  useMatch,
  useNavigate,
  useLocation,
} from 'react-router-dom';
import Header from 'src/components/Header';
import Footer from 'src/components/Footer';
import { connect } from 'react-redux';
import Axios from 'axios';
import { authconnect } from './Auth';
import {
  setLastVisitedLoanGuid,
  setWarning,
  setError,
  fetchBorrowerLoanApplications,
  setKioskMode,
  setKioskView,
} from 'src/actions';
import env from 'src/env';
import { AnonFeatureFlagsProvider } from 'src/appconfig';
import { apiAxios } from 'src/util';
import { useConfirmation } from 'src/hooks';
import LogRocket from 'logrocket';
import ErrorBoundary from 'src/components/ErrorBoundary';

const LoanContainer = lazy(() => import('src/components/Loan'));
const VerifyAccount = lazy(() => import('./components/Account/VerifyAccount'));
const Dashboard = lazy(() => import('./components/Dashboard'));
const ServerPicker = lazy(
  () => import('src/components/development/ServerPicker'),
);
const LoanValidationState = lazy(
  () => import('src/components/development/LoanValidationState'),
);
const InvitationCreator = lazy(
  () => import('src/components/development/InvitationCreator'),
);
const Test = lazy(() => import('src/components/development/Test'));
const SignupSignin = lazy(() => import('src/components/Account'));
const UserSettings = lazy(() => import('src/components/UserSettings'));
const ResetPassword = lazy(
  () => import('src/components/Account/ResetPassword'),
);
const RedeemInvitation = lazy(
  () => import('src/components/Account/Invitation'),
);
const Consent = lazy(() => import('src/components/Consent'));
const ESign = lazy(() => import('src/components/ESign'));
const CrossAuth = lazy(() => import('src/components/CrossAuth'));
const guidMatchStr =
  '[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}';

const LogoutComponent = ({ logout }) => {
  useEffect(() => {
    logout();
  }, [logout]);
  return <FMLoader />;
};

// Set default title for React 19
const useDefaultTitle = (defaultTitle, titleTemplate) => {
  useEffect(() => {
    document.title = defaultTitle;

    // Store the title template for potential use by child components
    if (titleTemplate) {
      window.__titleTemplate = titleTemplate;
    }
  }, [defaultTitle, titleTemplate]);
};

// Export this hook for use in other components
export const useDocumentTitle = (title) => {
  useEffect(() => {
    if (title) {
      const template = window.__titleTemplate;
      document.title = template ? template.replace('%s', title) : title;
    }

    // Cleanup function to reset to default title when component unmounts
    return () => {
      if (window.__titleTemplate) {
        document.title = 'Cinch by FM Home loans';
      }
    };
  }, [title]);
};

// Helper function to check if user is authenticated
const checkIsAuthenticated = (isAuthenticated) => {
  return typeof isAuthenticated === 'function' && isAuthenticated();
};

// Helper function to check if auth is properly initialized
const isAuthInitialized = (isAuthenticated) => {
  return typeof isAuthenticated === 'function';
};

function App ({
  dispatch,
  isAuthenticated,
  handleAuthentication,
  functionServer,
  apiServer,
  session,
  lastLoanGuid,
  logout,
  warning,
  error,
  appReady,
}) {
  const isCallback = Boolean(useMatch('/callback'));
  const navigate = useNavigate();
  const location = useLocation();
  const confirm = useConfirmation();

  // Function to handle logout based on kiosk mode
  const handleLogout = () => {
    const isKioskMode = sessionStorage.getItem('cinch-kiosk-mode') === 'true';

    if (isKioskMode) {
      // Clear all localStorage
      localStorage.clear();

      // Clear all sessionStorage (including kiosk mode)
      sessionStorage.clear();

      // Clear all cookies
      document.cookie.split(';').forEach(function (c) {
        document.cookie = c
          .replace(/^ +/, '')
          .replace(/=.*/, '=;expires=' + new Date().toUTCString() + ';path=/');
      });

      // Reset kiosk mode in Redux store
      dispatch(setKioskMode(false));
      dispatch(setKioskView(false));

      // Navigate to login without kiosk mode
      navigate('/login', { replace: true });
    } else {
      // Normal logout behavior
      logout();
    }
  };

  // Check for kiosk mode and NY affordable portal mode from URL parameter and set them immediately
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const isKioskMode = urlParams.get('mode') === 'kiosk';
    const existingKioskMode =
      sessionStorage.getItem('cinch-kiosk-mode') === 'true';
    const kioskViewPreference = sessionStorage.getItem(
      'cinch-kiosk-view-preference',
    );

    if (isKioskMode) {
      // URL has ?mode=kiosk - set kiosk mode
      sessionStorage.setItem('cinch-kiosk-mode', 'true');
      dispatch(setKioskMode(true));
      // If no preference is set, default to true for new kiosk sessions
      const shouldShowKioskView =
        kioskViewPreference !== null ? kioskViewPreference === 'true' : true;
      dispatch(setKioskView(shouldShowKioskView));
    } else if (existingKioskMode) {
      // Kiosk mode was previously set in sessionStorage - preserve it
      // This handles page refreshes and navigation without ?mode=kiosk
      dispatch(setKioskMode(true));
      // Respect user's previous toggle preference, default to true
      const shouldShowKioskView =
        kioskViewPreference !== null ? kioskViewPreference === 'true' : true;
      dispatch(setKioskView(shouldShowKioskView));
    } else {
      // No special mode in URL or session storage - ensure they're false
      dispatch(setKioskMode(false));
      dispatch(setKioskView(false));
      // Clean up preferences when not in special mode
      sessionStorage.removeItem('cinch-kiosk-view-preference');
    }
  }, [location.search, location.pathname, dispatch]);

  // Set up default document title for React 19
  useDefaultTitle('Cinch by FM Home loans', '%s - Cinch by FM Home Loans');

  const guidRouteMatch = useMatch(
    `/:section(apply|postapplication)/:guid(${guidMatchStr})`,
  );
  const referralRouteMatch = useMatch(
    '/register/:referralToken?/:guid?/:applicationIndex?/:pairFname?/:pairLname?/:pairEmail?',
  );
  const legacyReferralRouteMatch = useMatch(
    '/CreateAccount/:referralToken?/:guid?/:applicationIndex?/:pairFname?/:pairLname?/:pairEmail?',
  );
  const invitationRouteMatch = useMatch('/invite/:invitationHash?');
  const verificationRouteMatch = useMatch('/verify/:email?/:verificationCode?');
  const consentRouteMatch = useMatch('/consent/:hash');
  const esignRouteMatch = useMatch('/sign/:hash');
  const isCallbackCrossAuth = useMatch('/callback-cross-auth');

  useEffect(() => {
    (async () => {
      if (
        isCallback &&
        !checkIsAuthenticated(isAuthenticated) &&
        /access_token|id_token|error/.test(location.hash) &&
        !location.pathname.toLowerCase().includes('/login') &&
        !location.pathname.toLowerCase().includes('/register') &&
        !location.pathname.toLowerCase().includes('/createaccount') &&
        !location.pathname.toLowerCase().includes('/reset-password') &&
        !location.pathname.toLowerCase().includes('/consent') &&
        !location.pathname.toLowerCase().includes('/sign/') &&
        !location.pathname.toLowerCase().includes('/callback-cross-auth') &&
        location.pathname !== '/'
      ) {
        try {
          await handleAuthentication();
          navigate('/dashboard', { replace: true });
        } catch (error) {
          // Redirect to login with error state
          console.error('Authentication callback error:', error);
          navigate('/login', {
            replace: true,
            state: {
              authError:
                error.error_description ||
                error.error ||
                'Authentication failed. Please try again.',
            },
          });
        }
      }
    })();
  }, [isCallback, handleAuthentication, isAuthenticated]);

  useEffect(() => {
    Axios.defaults.baseURL = functionServer;
    apiAxios.defaults.baseURL = apiServer;
  }, [functionServer]);

  useEffect(() => {
    apiAxios.interceptors.request.use((config) => {
      if (
        LogRocket.sessionURL &&
        !LogRocket.sessionURL.includes('LogRocket.init()')
      ) {
        config.headers['X-LogRocket-URL'] = LogRocket.sessionURL;
      }
      return config;
    });

    // Add response interceptor to handle 401 errors for API calls
    const apiResponseInterceptor = apiAxios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          // User is unauthorized, handle logout based on kiosk mode
          handleLogout();
        }
        return Promise.reject(error);
      },
    );

    // Add response interceptor to handle 401 errors for function calls
    const functionResponseInterceptor = Axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          // User is unauthorized, handle logout based on kiosk mode
          handleLogout();
        }
        return Promise.reject(error);
      },
    );

    // Cleanup interceptors on unmount
    return () => {
      apiAxios.interceptors.response.eject(apiResponseInterceptor);
      Axios.interceptors.response.eject(functionResponseInterceptor);
    };
  }, [logout, navigate, handleLogout]);

  useEffect(() => {
    if (checkIsAuthenticated(isAuthenticated)) {
      const kioskMode = sessionStorage.getItem('cinch-kiosk-mode')
        ? true
        : false;
      dispatch(setKioskMode(kioskMode));
      dispatch(setKioskView(kioskMode));
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (guidRouteMatch) {
      dispatch(setLastVisitedLoanGuid(guidRouteMatch.params.guid));
    }
  }, [guidRouteMatch, session]);

  useEffect(() => {
    (async () => {
      if (warning) {
        await confirm({
          variant: 'alert',
          description: warning,
        });
        dispatch(setWarning(''));
      }
    })();
  }, [warning]);

  useEffect(() => {
    (async () => {
      if (error) {
        await confirm({
          variant: 'alert',
          description: error,
        });
        dispatch(setError(''));
      }
    })();
  }, [error]);

  useEffect(() => {
    if (
      checkIsAuthenticated(isAuthenticated) &&
      !session?.isLoanOfficer &&
      session?.idToken
    ) {
      dispatch(fetchBorrowerLoanApplications());
    }
  }, [isAuthenticated, session?.isLoanOfficer, session?.idToken]);

  useEffect(() => {
    if (checkIsAuthenticated(isAuthenticated) && session?.auth0UserId) {
      LogRocket.identify(session?.auth0UserId, {
        name: session?.userFullName,
        email: session?.userEmail,
      });
    }
  }, [isAuthenticated, session?.auth0UserId]);

  // Dispatch app_ready when authentication is confirmed and session is available
  useEffect(() => {
    if (checkIsAuthenticated(isAuthenticated) && session) {
      dispatch({ type: 'app_ready' });
    }
  }, [isAuthenticated, session]);

  // If auth is not properly initialized, show login component covering entire app
  if (!isAuthInitialized(isAuthenticated)) {
    return (
      <div className='app flex-container flex-vertical'>
        <ErrorBoundary>
          <Suspense fallback={<FMLoader backgroundColor='#fff' />}>
            <SignupSignin />
          </Suspense>
        </ErrorBoundary>
      </div>
    );
  }

  // If user is not authenticated, show login component regardless of appReady status
  if (!checkIsAuthenticated(isAuthenticated)) {
    // Check if we're on a protected route that should show the loader
    const isOnProtectedRoute = ![
      '/',
      '/login',
      '/signup',
      '/reset-password',
      referralRouteMatch?.url?.toLowerCase(),
      legacyReferralRouteMatch?.url?.toLowerCase(),
      invitationRouteMatch?.url?.toLowerCase(),
      verificationRouteMatch?.url?.toLowerCase(),
      consentRouteMatch?.url?.toLowerCase(),
      isCallbackCrossAuth?.url?.toLowerCase(),
      esignRouteMatch?.url?.toLowerCase(),
    ]
      .filter(Boolean)
      .includes(location.pathname?.toLowerCase());

    // If on protected route and app isn't ready, show loader
    if (isOnProtectedRoute && !appReady) {
      return <FMLoader />;
    }

    // Otherwise, render the normal app structure which includes login routes
  }

  if (checkIsAuthenticated(isAuthenticated) && location.pathname === '/') {
    return <Navigate to='/dashboard' replace />;
  }
  if (
    checkIsAuthenticated(isAuthenticated) &&
    location.pathname === '/' &&
    session?.isLoanOfficer
  ) {
    if (lastLoanGuid) return <Navigate to={`/apply/${lastLoanGuid}`} replace />;
  }
  if (
    checkIsAuthenticated(isAuthenticated) &&
    location.pathname === '/' &&
    session?.isLoanOfficer
  ) {
    return window.location.assign(env.LO_URL);
  }

  if (
    checkIsAuthenticated(isAuthenticated) &&
    location.search?.includes('kiosk') &&
    location.pathname === '/login'
  ) {
    logout();
  }

  return (
    <div className='app flex-container flex-vertical'>
      <ErrorBoundary>
        {!appReady && checkIsAuthenticated(isAuthenticated) ? (
          <FMLoader loadingMessage='Please wait, Cinch is loading...' noTimer />
        ) : (
          <Suspense fallback={<FMLoader backgroundColor='#fff' />}>
            <AnonFeatureFlagsProvider>
              {!checkIsAuthenticated(isAuthenticated) ? (
                // Unauthenticated routes - no header, just login/register/etc
                <Routes>
                  <Route path='/login' element={<SignupSignin />} />
                  <Route path='/signup' element={<SignupSignin />} />
                  <Route path='/' element={<SignupSignin />} />
                  <Route
                    path='/register/:referralToken?/:guid?/:applicationIndex?/:pairFname?/:pairLname?/:pairEmail?'
                    element={<SignupSignin />}
                  />
                  <Route
                    path='/CreateAccount/:referralToken?/:guid?/:applicationIndex?/:pairFname?/:pairLname?/:pairEmail?'
                    element={<SignupSignin />}
                  />
                  <Route
                    path='/verify/:email?/:verificationCode?'
                    element={<VerifyAccount />}
                  />
                  <Route path='/consent/:hash' element={<Consent />} />
                  <Route path='/sign/:hash' element={<ESign />} />
                  <Route path='/callback-cross-auth' element={<CrossAuth />} />
                  <Route
                    path='/invite/:invitationHash'
                    element={<SignupSignin />}
                  />
                  <Route path='/reset-password' element={<ResetPassword />} />
                  <Route path='*' element={<Navigate to='/login' replace />} />
                </Routes>
              ) : (
                // Authenticated routes - with header and main layout
                <>
                  <Header />
                  <main className='scroller flex flex-container flex-vertical'>
                    <Suspense fallback={<FMLoader backgroundColor='#fff' />}>
                      <Routes>
                        <Route path='/dashboard/*' element={<Dashboard />} />
                        <Route
                          path='/invite/:invitationHash'
                          element={<RedeemInvitation />}
                        />
                        <Route
                          path='/apply/:guid/*'
                          element={<LoanContainer />}
                        />
                        <Route
                          path='/postapplication/:guid/*'
                          element={<LoanContainer />}
                        />
                        <Route path='/consent/:hash' element={<Consent />} />
                        <Route path='/sign/:hash' element={<ESign />} />
                        <Route path='/settings' element={<UserSettings />} />
                        <Route path='/callback' element={<FMLoader />} />
                        {process.env.NODE_ENV === 'development' && (
                          <Route path='/test' element={<Test />} />
                        )}
                        <Route
                          path='/logout'
                          element={<LogoutComponent logout={logout} />}
                        />
                        <Route
                          path='*'
                          element={<Navigate to='/dashboard' replace />}
                        />
                      </Routes>
                      <Footer />
                    </Suspense>
                  </main>
                </>
              )}
              {process.env.NODE_ENV === 'development' && <ServerPicker />}
              {process.env.NODE_ENV === 'development' && (
                <LoanValidationState />
              )}
              {process.env.NODE_ENV === 'development' && <InvitationCreator />}
            </AnonFeatureFlagsProvider>
          </Suspense>
        )}
      </ErrorBoundary>
    </div>
  );
}

const mapStateToProps = ({
  app: {
    functionServer,
    apiServer,
    lastLoanGuid,
    warning,
    error,
    working,
    appReady,
  },
}) => ({
  functionServer,
  lastLoanGuid,
  apiServer,
  warning,
  error,
  savingLoan: working.some((item) => item.name?.startsWith('update')),
  appReady,
});
const mapAuthToProps = ({
  session,
  isAuthenticated,
  handleAuthentication,
  logout,
}) => ({
  session,
  idToken: session?.idToken,
  isAuthenticated,
  handleAuthentication,
  logout,
});

export default connect(mapStateToProps)(authconnect(mapAuthToProps)(App));
